import { useEffect, useMemo, useState } from 'react'
import FileDropZone from '../FileDropZone'
import { autoAdvanceDealIfReady } from '../../lib/dealStatusService'
import { supabase } from '../../lib/supabase'
import { Deal, User } from '../../types'
import { hasPermission } from '../../lib/permissions'
import {
  transitionDealStatus,
  getAvailableActions,
  statusLabel,
  canUserTransition,
  permissionForTransition,
  isDestructiveTransition,
  pipelineSideOf,
  DealStatus,
} from '../../lib/dealStatusService'
import BillOfSaleForm from './BillOfSaleForm'
import ESignatureCanvas from './ESignatureCanvas'
import DDDocumentCollection from './DDDocumentCollection'
import InspectionBooking from './InspectionBooking'
import WorkflowHistoryPanel from '../WorkflowHistoryPanel'
import PpsrLodgementPanel from './PpsrLodgementPanel'
import PayoutLetterPanel from './PayoutLetterPanel'
import BuyerDetailsPanel from './BuyerDetailsPanel'
import SignedDocumentPanel from './SignedDocumentPanel'
import CounterSignPanel from './CounterSignPanel'
import SalePanel from './SalePanel'
import DealEditPanel from './DealEditPanel'
import PurchaseAgreementPanel from './PurchaseAgreementPanel'
import { generateVehicleImage, clearVehicleImage } from '../../services/vehicleImageService'
import { pathForDeal } from '../../routes'
import './DealWorkspace.css'
import ActionCard from '../ActionCard'
import type { ActionStatus } from '../ActionCard'
import { sellerIssuesOwnInvoice } from '../../lib/sellerType'
import DealSmsHistory from './DealSmsHistory'
import VehicleReturnDialog from './VehicleReturnDialog'
import { SIGNED_STATUSES, ACQUIRED_STATUSES } from '../../lib/dealLifecycle'
import {
  ACTIONS,
  ActionEvent,
  countByAction,
  lastByAction,
  loadActionHistory,
  logAction,
} from '../../services/actionLog'

interface DealWorkspaceProps {
  user: User
  deal: Deal
  onClose: () => void
  onChanged: () => void
}

/* Whether the signed contract is actually on file. Deliberately not part of the audit
   log: "it was signed" and "we hold the document" are two different facts, and for six
   deals on this system they were not the same one. */
interface BosDocRow {
  bos_pdf_path: string | null
  bos_pdf_filed_at: string | null
  seller_signed_at: string | null
  seller_signed_by_name: string | null
  buyer_signed_at?: string | null
  buyer_signed_by_name?: string | null
  executed_pdf_path?: string | null
  seller_signature_canvas_svg?: string | null
}

async function loadBosDoc(dealId: string): Promise<BosDocRow | null> {
  try {
    const { data } = await supabase
      .from('bill_of_sale_documents')
      .select('bos_pdf_path, bos_pdf_filed_at, seller_signed_at, seller_signed_by_name, seller_signature_canvas_svg, buyer_signed_at, buyer_signed_by_name, executed_pdf_path')
      .eq('deal_id', dealId)
      .order('created_at', { ascending: false })
      .limit(20)

    /* A deal can carry several of these rows: one written when the link was sent, and one
       per signing attempt, and they are not interchangeable. Deal 5c7f5782 has a row
       stamped later than the one holding the signature that was actually drawn, so
       ordering by time alone made the card describe the wrong record. Rank instead: a
       drawn signature beats a bare timestamp, ties go to the most recently signed.
       This must match pickSignedRow in functions/api/deals/bos-document.js, or the card
       and the document that gets filed will describe different things. */
    let best: BosDocRow | null = null
    let bestRank = -1
    for (const row of (data || []) as BosDocRow[]) {
      const rank = row.seller_signature_canvas_svg ? 2 : row.seller_signed_at ? 1 : 0
      if (rank > bestRank) {
        best = row
        bestRank = rank
      } else if (rank === bestRank && best && (row.seller_signed_at || '') > (best.seller_signed_at || '')) {
        best = row
      }
    }
    return best
  } catch (err) {
    // Not fatal: the card falls back to Not started and the panel tells the truth.
    return null
  }
}
type Panel =
  | 'overview'
  | 'bos'
  | 'signature'
  | 'dd'
  | 'inspection'
  | 'buyer'
  | 'edit'
  | 'sale'
  | 'signed'
  | 'countersign'
  | 'agreement'

const dYear = (d: Deal) => d.autograb_year ?? d.vehicle_year
const dMake = (d: Deal) => d.autograb_make || d.vehicle_make || ''
const dModel = (d: Deal) => d.autograb_model || d.vehicle_model || ''
const dVin = (d: Deal) => d.autograb_vin || d.vehicle_vin || ''
/*
 * The agreed purchase price, not the AutoGrab market value.
 *
 * Winnie, 10 Oct: "The price shown should be the agreed purchase price not the market
 * value price once the deal is created." The agreed buy price lives in valuation_amount
 * (badly named - see functions/api/_lib/sellerPortal.js); market value was only ever the
 * starting point for the offer. This helper also feeds the payment run this screen
 * initiates and the price passed to the Vehicle Purchase Agreement, so reading market
 * value first meant a car bought below market would have been paid out at market.
 * Market value is still the fallback for a deal that has no agreed price yet.
 */
/* Always a number, as before: the Vehicle Purchase Agreement screen requires one. */
const dPrice = (d: Deal): number => d.valuation_amount || d.autograb_market_value || 0
const money = (n?: number) => (n != null ? `$${Math.round(n).toLocaleString('en-AU')}` : '—')
/*
 * Where the car actually is, for the inspection request.
 *
 * A seller address can live in four columns and this screen only ever read one of
 * them, so a deal whose address sat in the newer split columns sent the inspector
 * the words "see deal" - an instruction they cannot follow, having no account and
 * no link. Street line first, then locality, which is the order the Vehicle Purchase Agreement
 * renderer already uses, so the contract and the inspection request cannot end up
 * disagreeing about where the car is.
 *
 * seller_location is the old free-text field and already reads as a whole address,
 * so it is used on its own rather than glued to the locality columns twice over.
 */
function sellerLocationFor(d: Deal): string {
  const street = (d.seller_address || '').trim()
  const locality = [d.seller_suburb, d.seller_state, d.seller_postcode]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ')
  if (street) return [street, locality].filter(Boolean).join(', ')
  const freeText = (d.seller_location || '').trim()
  if (freeText) return freeText
  return locality
}


export default function DealWorkspace({ user, deal: dealProp, onClose, onChanged }: DealWorkspaceProps) {
  /*
   * The deal this workspace renders.
   *
   * The prop is a snapshot the pipeline handed over when the card was opened, and
   * it does not change underneath us: onChanged() reloads the board, not this
   * panel. So after sending the Vehicle Purchase Agreement the banner read "deal is Offer
   * Accepted" while the stage line above it still said Sourced, BOS not sent -
   * the record was right and the screen was a step behind, which is exactly how
   * somebody ends up pressing send a second time.
   *
   * Anything a child completes now refetches this one row and renders from that.
   */
  const [fresh, setFresh] = useState<Deal | null>(null)
  const deal = fresh || dealProp

  /* A different card was opened: drop the copy so we do not show the last one. */
  useEffect(() => {
    setFresh(null)
  }, [dealProp.id])

  const [panel, setPanel] = useState<Panel>('overview')
  /* Carried from the repeat prompt into the send, which refuses a resend without it. */
  const [bosRepeatReason, setBosRepeatReason] = useState<string | undefined>(undefined)
  const [showInspectionOutcome, setShowInspectionOutcome] = useState(false)
  const [failReason, setFailReason] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [noticeTone, setNoticeTone] = useState<'info' | 'error'>('info')
  const [busy, setBusy] = useState(false)
  const [confirmTarget, setConfirmTarget] = useState<DealStatus | null>(null)
  const [showReturn, setShowReturn] = useState(false)
  const [copied, setCopied] = useState(false)
  const [imageBusy, setImageBusy] = useState(false)
  const [events, setEvents] = useState<ActionEvent[]>([])
  const [bosDoc, setBosDoc] = useState<BosDocRow | null>(null)
  const [openingContract, setOpeningContract] = useState(false)
  const [waiveReason, setWaiveReason] = useState('')
  const [waiving, setWaiving] = useState(false)

  // The payout mismatch hold. resolveChoice is the answer a human confirms is
  // correct after checking with the seller or the financier; resolveNote is how.
  const [resolveChoice, setResolveChoice] = useState<boolean | null>(null)
  const [resolveNote, setResolveNote] = useState('')
  const [resolving, setResolving] = useState(false)

  // Winnie, 1 Sep: the inspection report itself, attached to the deal.
  const [inspectionReport, setInspectionReport] = useState<
    { id: string; path: string; name: string | null; at: string } | null
  >(null)
  const [reportBusy, setReportBusy] = useState(false)

  /* Winnie, 11 Sep: the tax invoice is the only documentation a dealer, auction or
     tender purchase needs, and it is the thing that carries the deal to Committed.
     Normally it is filed as the deal is created. When it was not - the deal was typed in
     first, or the filing failed - there has to be somewhere else to put it, because the
     DD Documents step those deals would otherwise use is greyed out for them. */
  const [taxInvoiceFiled, setTaxInvoiceFiled] = useState(false)
  const [taxInvoiceBusy, setTaxInvoiceBusy] = useState(false)

  /*
   * Open the executed contract.
   *
   * The card knew a signed contract was on file and said so, but the only
   * control on it was the action itself - which, being already done, asks why
   * you are doing it again before it will run. So the one thing a person came
   * to the card for was the one thing the card would not do.
   *
   * The file sits in a private bucket, so there is no plain link to hand out.
   * We ask for a short-lived signed URL at the moment it is wanted, which also
   * means the address expires instead of living on in a browser history.
   */
  /*
   * A waiver is recorded, not implied. It says a person decided this deal may
   * sign without the document pack, who decided it, and why. It does not touch
   * dd_status and it does not invent uploads: what is missing stays missing and
   * stays visible, on this card and in the seller portal.
   */
  const waiveDocuments = async () => {
    const reason = waiveReason.trim()
    if (reason.length < 5) {
      setNoticeTone('error')
      setNotice('Say why the documents are being waived — a few words is enough.')
      return
    }
    setWaiving(true)
    try {
      const { error } = await supabase
        .from('deals')
        .update({
          dd_waived_at: new Date().toISOString(),
          dd_waived_by_user_id: user.id,
          dd_waived_reason: reason,
        })
        .eq('id', deal.id)
      if (error) throw error
      setWaiveReason('')
      setNoticeTone('info')
      setNotice('Documents waived. The seller can sign without them; the pack still reads as outstanding.')
      onChanged()
    } catch {
      setNoticeTone('error')
      setNotice('Could not record the waiver. Nothing was changed.')
    } finally {
      setWaiving(false)
    }
  }

  const openSignedContract = async () => {
    const path = bosDoc?.bos_pdf_path
    if (!path) {
      setNoticeTone('error')
      setNotice('There is no filed contract to open yet.')
      return
    }
    setOpeningContract(true)
    try {
      const { data, error } = await supabase.storage
        .from('bill-of-sale')
        .createSignedUrl(path, 120)
      if (error || !data?.signedUrl) throw new Error('No link came back')
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    } catch {
      setNoticeTone('error')
      setNotice('Could not open the contract just now. It is still on file - try again in a moment.')
    } finally {
      setOpeningContract(false)
    }
  }

  /* What has already been done to this deal. Read when it opens, and again after
     anything is done from here, so a card can say 'Booked 22 Aug by Winnie' instead of
     sitting there looking untouched. */
  useEffect(() => {
    let live = true
    loadActionHistory(deal.id).then((rows) => {
      if (live) setEvents(rows)
    })
    void loadBosDoc(deal.id).then((row) => {
      if (live) setBosDoc(row)
    })
    void loadInspectionReport(deal.id).then((row) => {
      if (live) setInspectionReport(row)
    })
    void loadTaxInvoiceFiled(deal.id).then((filed) => {
      if (live) setTaxInvoiceFiled(filed)
    })
    return () => {
      live = false
    }
  }, [deal.id])

  const refreshHistory = async () => {
    setEvents(await loadActionHistory(deal.id))
    setBosDoc(await loadBosDoc(deal.id))
  }

  /*
   * The inspection report (Winnie, 1 Sep).
   *
   * The outcome was recordable but the document behind it was not, so "Good - no issues
   * found" was somebody's word for what a report said with no way to read the report.
   *
   * It goes in dd_documents like every other deal document, but deliberately outside the
   * DD Review queue: this is evidence about the car, not identity evidence about the
   * seller, and putting it in that queue would ask a reviewer to approve the wrong thing.
   */
  const loadInspectionReport = async (dealId: string) => {
    const { data } = await supabase
      .from('dd_documents')
      .select('id, document_file_path, original_filename, uploaded_at')
      .eq('deal_id', dealId)
      .eq('document_type', 'inspection_report')
      .is('deleted_at', null)
      .order('uploaded_at', { ascending: false })
      .limit(1)
    const row = data && data[0]
    return row
      ? { id: row.id, path: row.document_file_path, name: row.original_filename, at: row.uploaded_at }
      : null
  }

  const uploadInspectionReport = async (file: File) => {
    setReportBusy(true)
    setNotice(null)
    try {
      const ext = (file.name.split('.').pop() || 'pdf').toLowerCase()
      const path = `dd-docs/${deal.id}/inspection_report-${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from('dd-documents').upload(path, file)
      if (upErr) throw upErr

      const userId = (await supabase.auth.getUser()).data.user?.id
      const { error: rowErr } = await supabase.from('dd_documents').insert({
        deal_id: deal.id,
        document_type: 'inspection_report',
        document_file_path: path,
        uploaded_by_role: 'staff',
        uploaded_via: 'workspace',
        uploaded_at: new Date().toISOString(),
        original_filename: String(file.name).slice(0, 255),
        file_size_bytes: file.size,
        content_type: file.type || null,
        /* Not 'validated' - nothing validated it. It is a report somebody attached, and
           saying otherwise would put a word on the record that is not true. */
        validation_status: 'not_applicable',
        manual_review_required: false,
        reviewed_by_user_id: userId ?? null,
      })
      if (rowErr) throw rowErr

      setInspectionReport(await loadInspectionReport(deal.id))
      say('Inspection report attached to the deal.')
    } catch (err) {
      say(err instanceof Error ? err.message : 'The report could not be attached.', 'error')
    } finally {
      setReportBusy(false)
    }
  }

  /* Whether this deal's tax invoice is already on file. The same document type the
     create form and functions/api/deals/tax-invoice use, so all three agree about what
     counts as the invoice. */
  const loadTaxInvoiceFiled = async (dealId: string) => {
    const { count } = await supabase
      .from('dd_documents')
      .select('id', { count: 'exact', head: true })
      .eq('deal_id', dealId)
      .eq('document_type', 'tax_invoice_or_abn_invoice')
      .is('deleted_at', null)
    return !!count
  }

  /* Filing goes through the API rather than straight into storage, so the document check,
     the storage path and the row written are the same ones the create form produces. Two
     ways to file the same document that disagree about any of that is a bug waiting. */
  const fileTaxInvoice = async (file?: File) => {
    if (!file) return
    setTaxInvoiceBusy(true)
    setNotice(null)
    try {
      const dataUrl: string = await new Promise((res, rej) => {
        const r = new FileReader()
        r.onload = () => res(String(r.result || ''))
        r.onerror = () => rej(new Error('That file could not be read.'))
        r.readAsDataURL(file)
      })

      const { data: sess } = await supabase.auth.getSession()
      const tok = sess.session?.access_token
      const res = await fetch('/api/deals/tax-invoice', {
        method: 'POST',
        headers: tok
          ? { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok }
          : { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealId: deal.id,
          dataBase64: dataUrl,
          filename: file.name,
          contentType: file.type,
        }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok || !body || !body.success) {
        throw new Error(
          (body && body.error) || 'The invoice could not be filed against this deal.'
        )
      }

      setTaxInvoiceFiled(true)

      /* The invoice is what moves the deal, so ask the status machinery rather than
         stamping anything by hand: both preconditions run and both write their own line
         in the status log, and a deal already past Committed simply stays where it is. */
      const moved = await autoAdvanceDealIfReady(deal.id)
      /* 5 Nov 2026: the invoice is read against the deal as it is filed. Say which empty
         boxes it filled, and put any disagreement (VIN, total, ABN) in front of the
         person rather than only in the review queue. */
      const filled: string[] = Array.isArray(body.filled) ? body.filled : []
      const clashes: string[] = (Array.isArray(body.issues) ? body.issues : [])
        .filter((i: { code?: string }) => /^invoice_/.test(String(i && i.code)))
        .map((i: { message?: string }) => String(i.message || ''))
      const head =
        moved && moved.success
          ? 'Tax invoice filed - the deal has moved to Committed.'
          : 'Tax invoice filed against the deal.'
      const fillNote = filled.length ? ' Filled from the invoice: ' + filled.join(', ') + '.' : ''
      if (clashes.length) say(head + fillNote + ' ' + clashes.join(' '), 'error')
      else say(head + fillNote)
      onChanged()
      await refreshHistory()
    } catch (err) {
      say(err instanceof Error ? err.message : 'The invoice could not be filed.', 'error')
    } finally {
      setTaxInvoiceBusy(false)
    }
  }

  const openInspectionReport = async () => {
    if (!inspectionReport) return
    const { data, error } = await supabase.storage
      .from('dd-documents')
      .createSignedUrl(inspectionReport.path, 300)
    if (error || !data || !data.signedUrl) {
      say('That report could not be opened just now.', 'error')
      return
    }
    window.open(data.signedUrl, '_blank', 'noopener')
  }

  const last = useMemo(() => lastByAction(events), [events])
  const counts = useMemo(() => countByAction(events), [events])
  /* What the deal record itself says about each action.

     This is the part that was missing. A booking was made and the card still read
     'Not done yet', because the card only believed the audit log - while the record
     had known the inspection was booked all along. The record decides the status; the
     audit log is used only for the one thing it alone knows, which is who and when. */
  const when = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString('en-AU', {
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit',
        })
      : ''

  const inspectionBooked = Boolean(
    deal.inspection_status && deal.inspection_status !== 'not_started',
  )
  const ddApproved = deal.dd_status === 'approved'
  const ddStarted = Boolean(deal.dd_status && deal.dd_status !== 'not_started')
  /* Whether the SELLER SIGNED is a fact about bill_of_sale_documents, not about which
     stage the deal has reached. A deal inserted straight at paid has no signature and the
     card used to say "The seller has signed" anyway, with the tooltip underneath admitting
     WHEN: Not recorded, WHO: Not recorded. Telling staff a seller signed when nobody did is
     the one thing this card must never do.

     The stage still marks the step done, so nothing regresses for the deals signed before
     the app kept records - but it no longer claims a signature it cannot produce. */
  const signatureOnFile = !!(bosDoc && bosDoc.seller_signed_at)
  const stageSaysSigned = SIGNED_STATUSES.includes(deal.deal_status)
  const signed = signatureOnFile || stageSaysSigned


  /* Every action taken from a card goes through here, so the event, the person and any
     repeat reason are recorded in one place rather than in nine.

     Success here means the handler finished without throwing. The cards that only open
     a panel do not record anything yet - the panel that does the work has to be the one
     that says it happened, and until it does, that card honestly reads as not done. */
  const runAction = async (
    action: string,
    go: () => void | Promise<void>,
    // of is nullable because a card can be Completed from the deal record alone,
    // with no earlier event to point the repeat at.
    repeat?: { of: string | null; reason: string },
    details?: Record<string, unknown>,
  ) => {
    let ok = true
    let failure: string | null = null
    try {
      await go()
    } catch (err) {
      ok = false
      failure = err instanceof Error ? err.message : 'The action did not complete'
    }
    await logAction({
      dealId: deal.id,
      action,
      actorId: user.id,
      success: ok,
      errorMessage: failure,
      details: details || null,
      repeatOf: repeat ? repeat.of : null,
      repeatReason: repeat ? repeat.reason : null,
    })
    await refreshHistory()
  }


  const say = (msg: string, tone: 'info' | 'error' = 'info') => {
    setNotice(msg)
    setNoticeTone(tone)
  }

  const refresh = async () => {
    onChanged()
    setPanel('overview')
    const { data } = await supabase.from('deals').select('*').eq('id', dealProp.id).maybeSingle()
    if (data) setFresh(data as Deal)
  }

  const childDone = async (msg: string) => {
    say(msg)
    await refresh()
  }

  /**
   * Resolve a payout mismatch (Winnie, 1 Sep).
   *
   * Sales and the seller answered the finance question differently, so the deal is
   * held. Clearing the hold is deliberately not a tick: somebody has to say which
   * answer is correct and how they know, because that answer then decides whether a
   * payout letter is required before any money leaves.
   */
  const resolvePayoutMismatch = async () => {
    if (resolveChoice !== true && resolveChoice !== false) {
      say('Choose which answer is correct before clearing the hold.', 'error')
      return
    }
    if (resolveNote.trim().length < 10) {
      say('Record how the payout position was confirmed - a few words is enough.', 'error')
      return
    }
    setResolving(true)
    setNotice(null)
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id
      const now = new Date().toISOString()
      const { error } = await supabase
        .from('deals')
        .update({
          // The confirmed answer becomes the deal's payout position, so the payment
          // block downstream reads the resolved truth rather than the disagreement.
          finance_payout_applicable: resolveChoice,
          payout_mismatch_resolved_at: now,
          payout_mismatch_resolved_by: userId ?? null,
          payout_mismatch_resolution: resolveNote.trim(),
          updated_at: now,
        })
        .eq('id', deal.id)
      if (error) throw error

      // The hold is an event, so how it ended belongs in the history too.
      await supabase.from('workflow_logs').insert({
        deal_id: deal.id,
        trigger_name: 'payout_mismatch_resolved',
        trigger_status: 'success',
        success: true,
        details: {
          sales_answer: deal.finance_payout_applicable ?? null,
          seller_answer: deal.seller_finance_payout_applicable ?? null,
          confirmed: resolveChoice,
          resolution: resolveNote.trim(),
        },
      })

      say(
        resolveChoice
          ? 'Hold cleared. Finance is owing, so a payout letter must be approved before this deal can be paid.'
          : 'Hold cleared. The vehicle is confirmed as owned outright.',
      )
      setResolveNote('')
      setResolveChoice(null)
      await refresh()
    } catch (err) {
      say(err instanceof Error ? err.message : 'Could not clear the hold. Nothing was changed.', 'error')
    } finally {
      setResolving(false)
    }
  }

  /**
   * Record the inspection outcome (§2.3).
   *
   * "Mark inspection complete" used to be a single button with no result, so a
   * failed inspection advanced the deal exactly like a clean one — the two were
   * indistinguishable afterwards. A fail now has to carry a reason before the
   * deal can go on, which is what the spec asks for: proceed with a reason, or
   * cancel.
   */
  const recordInspectionOutcome = async (outcome: 'pass' | 'minor_issues' | 'failed', reason?: string) => {
    if (outcome === 'minor_issues' && !(reason || '').trim()) {
      say('A failed inspection needs a reason before the deal can proceed.', 'error')
      return
    }
    setBusy(true)
    setNotice(null)
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id
      const { error } = await supabase
        .from('deals')
        .update({
          inspection_status: 'completed',
          inspection_outcome: outcome,
          inspection_outcome_at: new Date().toISOString(),
          inspection_outcome_by: userId ?? null,
          inspection_proceed_reason: outcome === 'minor_issues' ? (reason || '').trim() : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', deal.id)
      if (error) throw error
      say(
        outcome === 'pass'
          ? 'Inspection passed — the deal can move to Approved for Payment.'
          : outcome === 'minor_issues'
            ? 'Minor issues recorded. The reason for proceeding is on the deal.'
            : 'Inspection failed — this deal cannot proceed. Cancel it, or record a different outcome if this was entered in error.',
        outcome === 'failed' ? 'error' : undefined,
      )

      /*
       * A history line for the outcome. deals.inspection_outcome only ever holds the
       * latest value, so recording a second outcome overwrites the first and the change
       * leaves no trace at all. This row keeps what it was and what it became.
       */
      const previousOutcome = deal.inspection_outcome ?? null
      const { error: logError } = await supabase.from('workflow_logs').insert({
        deal_id: deal.id,
        trigger_name: 'inspection_outcome_recorded',
        trigger_status: 'success',
        success: true,
        details: {
          outcome,
          previous_outcome: previousOutcome,
          changed: previousOutcome !== null && previousOutcome !== outcome,
          reason: outcome === 'minor_issues' ? (reason || '').trim() : null,
          recorded_by: userId ?? null,
        },
        executed_at: new Date().toISOString(),
      })
      if (logError) {
        /* The outcome itself is saved. Say so, rather than let the row look complete. */
        say(
          'The outcome is recorded on the deal, but the history line for it was not written, so this change will not appear in the deal history.',
          'error'
        )
      }
      setFailReason('')
      setShowInspectionOutcome(false)
      onChanged()
    } catch (err) {
      say(err instanceof Error ? err.message : 'Could not record the inspection outcome', 'error')
    } finally {
      setBusy(false)
    }
  }

  const initiatePaymentRun = async () => {
    setBusy(true)
    setNotice(null)
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id
      const price = dPrice(deal) || 0
      // v2.0-NL.2.0 removed the holding deposit, so the payment run pays the
      // agreed price in full. The column stays at zero for old rows.
      const holding = 0
      const { data, error } = await supabase
        .from('payment_runs')
        .insert([{
          deal_id: deal.id,
          purchase_price: price,
          holding_deposit: holding,
          total_amount: Math.max(price - holding, 0),
          initiated_by_user_id: userId,
          status: 'pending_winnie',
        }])
        .select('id')
        .single()
      if (error) throw error
      const { error: linkError } = await supabase
        .from('deals')
        .update({ payment_run_id: data.id, updated_at: new Date().toISOString() })
        .eq('id', deal.id)
      /*
       * The payment run row exists either way - it was created above. What this write
       * does is point the deal at it, and it was not being checked, so a deal could be
       * left unlinked while the person was told the run had started. Do not say it
       * started cleanly when the link is missing: somebody has to go and join them up.
       */
      if (linkError) {
        say('The payment run was created, but this deal was not linked to it: ' + linkError.message + ' The run is in Payment Run and needs the deal attaching by hand.', 'error')
        onChanged()
        return
      }
      say('Payment run initiated — now pending first approval in Payment Run.')
      onChanged()
    } catch (err) {
      say(err instanceof Error ? err.message : 'Could not initiate payment run', 'error')
    } finally {
      setBusy(false)
    }
  }

  const advance = async (target: DealStatus) => {
    setBusy(true)
    setNotice(null)
    setConfirmTarget(null)
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id
      const result = await transitionDealStatus(
        deal.id,
        deal.deal_status as DealStatus,
        target,
        userId,
        user
      )
      say(result.message, result.success ? 'info' : 'error')
      if (result.success) onChanged()
    } catch (err) {
      say(err instanceof Error ? err.message : 'Transition failed', 'error')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Generate an illustration for a vehicle with no photograph.
   *
   * Stored separately from photo_url and badged "AI" wherever it appears — it is
   * a picture of a car like this one, not of this one.
   */
  const makeImage = async () => {
    setImageBusy(true)
    setNotice(null)
    try {
      const res = await generateVehicleImage(deal, user.id)
      say(res.message, res.ok ? 'info' : 'error')
      if (res.ok) onChanged()
    } catch (err) {
      say(err instanceof Error ? err.message : 'Image generation failed', 'error')
    } finally {
      setImageBusy(false)
    }
  }

  const dropImage = async () => {
    setImageBusy(true)
    try {
      await clearVehicleImage(deal.id)
      say('Generated image removed.')
      onChanged()
    } catch (err) {
      say(err instanceof Error ? err.message : 'Could not remove the image', 'error')
    } finally {
      setImageBusy(false)
    }
  }

  /** Cancelling a deal is terminal — it has no valid next status — so ask first. */
  const requestAdvance = (target: DealStatus) => {
    if (isDestructiveTransition(target, deal.deal_status as DealStatus)) setConfirmTarget(target)
    else advance(target)
  }

  const nextStatuses = getAvailableActions(deal.deal_status as DealStatus)

  /* Winnie, 11 Sep, section 3.
   *
   * A dealer, an auction house and a tender seller issue their own tax invoice and
   * never sign our Vehicle Purchase Agreement. Five steps on this screen exist only to
   * produce and collect that signature, so for those three seller types they do not
   * apply at all.
   *
   * They are greyed rather than hidden. A step that vanishes reads as a missing
   * feature, and anyone switching the seller type back to Type 1 or 2 needs to see what
   * is about to come back. Because this is worked out from deal.seller_type on every
   * render, that switch un-greys all five the moment the edit is saved.
   */
  const licensedSeller = sellerIssuesOwnInvoice(deal.seller_type)
  const notApplicable =
    'Not needed for a dealer, auction or tender purchase. The tax invoice the seller issues stands in its place.'

  /* Something already done stays Completed even for a licensed seller. A deal that was
     Type 1 yesterday really does hold that signature, and greying it out would be a lie
     about what is on the file. Only steps that have not happened go grey. */
  const step = (actual: ActionStatus): ActionStatus =>
    licensedSeller && actual !== 'completed' && actual !== 'in_progress' ? 'not_required' : actual


  const renderPanel = () => {
    switch (panel) {
      case 'bos':
        return <BillOfSaleForm deal={deal} repeatReason={bosRepeatReason} onComplete={() => childDone('Vehicle Purchase Agreement generated — deal is Offer Accepted.')} />
      case 'signature':
        return (
          <ESignatureCanvas
            dealId={deal.id}
            sellerName={deal.seller_name}
            vehicleInfo={{ year: dYear(deal), make: dMake(deal), model: dModel(deal), price: dPrice(deal) }}
            billOfSaleData={{ depositElected: false, lodgementElected: !!deal.lodgement_elected }}
            onSignatureComplete={() => childDone('Signature captured — deal is Contract Signed.')}
          />
        )
      case 'dd':
        return (
          <DDDocumentCollection
            dealId={deal.id}
            sellerType={deal.seller_type || 'individual'}
            sellerInfo={{ name: deal.seller_name, email: deal.seller_email || '', phone: deal.seller_phone || '' }}
            onCollectionComplete={() => childDone('DD documents submitted — now in DD Review.')}
          />
        )
      case 'buyer':
        return <BuyerDetailsPanel deal={deal} user={user} onSaved={(m) => childDone(m)} />
      case 'signed':
        return <SignedDocumentPanel deal={deal} user={user} onSaved={(m) => childDone(m)} />
      case 'countersign':
        return <CounterSignPanel deal={deal} onSaved={(m) => childDone(m)} />
      case 'sale':
        return (
          <SalePanel
            deal={deal}
            user={user}
            onSaved={(m) => say(m)}
            onConfirmSale={() => advance('sold')}
          />
        )
      case 'agreement':
        return (
          <PurchaseAgreementPanel
            deal={deal}
            user={user}
            onSaved={(m) => childDone(m)}
          />
        )
      case 'edit':
        return (
          <DealEditPanel
            deal={deal}
            user={user}
            onSaved={(m) => childDone(m)}
            onArchived={() => {
              onChanged()
              onClose()
            }}
          />
        )
      case 'inspection':
        return (
          <InspectionBooking
            dealId={deal.id}
            actorId={user.id}
            sellerLocation={sellerLocationFor(deal)}
            vehicleInfo={{ year: dYear(deal), make: dMake(deal), model: dModel(deal), vin: dVin(deal) }}
            sellerInfo={{ name: deal.seller_name, phone: deal.seller_phone || '', email: deal.seller_email || '' }}
            onBookingComplete={() => childDone('Inspection booked.')}
          />
        )
      default:
        return (
          <div className="workspace-overview">
            <div className="workspace-status-row">
              <div>
                <span className="ws-label">Current stage</span>
                <span className="ws-status-badge">{statusLabel(deal.deal_status)}</span>
              </div>
              <div className="ws-substatus">
                <span>DD: <strong>{deal.dd_status || 'not started'}</strong></span>
                <span>Inspection: <strong>{deal.inspection_status || 'not started'}</strong></span>
                {/* This read 'sent' for the rest of the deal's life. Sent is the least
                    interesting thing a contract can be once it is signed, and the header is
                    where someone looks first. bosDoc is already loaded above for the
                    signed-contract card, so the three real states cost nothing here. */}
                <span>BOS: <strong>{bosDoc?.buyer_signed_at ? 'executed' : bosDoc?.seller_signed_at ? 'signed by seller' : deal.bill_of_sale_sent_at ? 'sent' : 'not sent'}</strong></span>
              </div>
            </div>

            {deal.payout_mismatch_held_at && !deal.payout_mismatch_resolved_at && (
              <div className="ws-payout-hold">
                <strong>This deal is on hold - the two payout answers disagree.</strong>
                <div className="ws-payout-hold-answers">
                  <span>
                    Sales said:{' '}
                    <strong>
                      {deal.finance_payout_applicable ? 'finance owing' : 'owned outright'}
                    </strong>
                  </span>
                  <span>
                    The seller said:{' '}
                    <strong>
                      {deal.seller_finance_payout_applicable ? 'finance owing' : 'owned outright'}
                    </strong>
                  </span>
                </div>
                <p>
                  Nothing moves on this deal until somebody confirms which is right. Check with the
                  seller or their financier, then record the answer and how you confirmed it.
                </p>
                <div className="ws-payout-hold-form">
                  <select
                    value={resolveChoice === null ? '' : resolveChoice ? 'yes' : 'no'}
                    onChange={(e) =>
                      setResolveChoice(e.target.value === '' ? null : e.target.value === 'yes')
                    }
                  >
                    <option value="">Which answer is correct?</option>
                    <option value="yes">Finance is owing - a payout letter is required</option>
                    <option value="no">The vehicle is owned outright</option>
                  </select>
                  <textarea
                    value={resolveNote}
                    onChange={(e) => setResolveNote(e.target.value)}
                    placeholder="How was this confirmed? For example: spoke to the financier, payout figure quoted."
                  />
                  <div className="ws-payout-hold-actions">
                    <button
                      className="ws-advance-btn danger"
                      disabled={resolving}
                      onClick={resolvePayoutMismatch}
                    >
                      {resolving ? 'Clearing the hold...' : 'Clear the hold'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {deal.payout_mismatch_held_at && deal.payout_mismatch_resolved_at && (
              <div className="ws-payout-hold-resolved">
                <strong>Payout mismatch resolved.</strong> Confirmed as{' '}
                {deal.finance_payout_applicable ? 'finance owing' : 'owned outright'} -{' '}
                {deal.payout_mismatch_resolution}
              </div>
            )}

            {!deal.buyer_email && ACQUIRED_STATUSES.includes(deal.deal_status) && (
              <div className="ws-buyer-warning">
                No buyer email on this deal, so its invoice cannot be sent and will never appear in
                AR Reminders. Add the buyer details to release it.
              </div>
            )}

            {deal.generated_image_url && !deal.photo_url && (
              <div className="ws-genimage">
                <img src={deal.generated_image_url} alt="" />
                <span className="ws-genimage-note">
                  AI-generated illustration — not a photograph of this vehicle. Do not use it on a
                  listing in place of a real photo.
                </span>
              </div>
            )}

            {licensedSeller && (
              <div className="ws-fasttrack-note">
                <p>
                  A dealer, auction or tender purchase. The seller issues their own tax
                  invoice, so Vehicle Purchase Agreement, Capture Signature, Signed Contract,
                  Book Inspection and DD Documents do not apply here and are greyed out below.
                  Change the seller type to a private seller and all five come back.
                </p>
                {taxInvoiceFiled ? (
                  <p className="ws-fasttrack-ok">The tax invoice is on file.</p>
                ) : (
                  <>
                    <p className="ws-fasttrack-ask">
                      No tax invoice is on file yet. It is the only documentation this deal
                      needs, and filing it moves the deal to Committed.
                    </p>
                    <FileDropZone
                      accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                      label="Upload tax invoice"
                      busy={taxInvoiceBusy}
                      busyLabel="Filing the invoice..."
                      hint="The seller's own tax invoice. A PDF or a photo, up to 15 MB."
                      onFiles={(files) => fileTaxInvoice(files[0])}
                    />
                  </>
                )}
              </div>
            )}

            <div className="workspace-actions-grid">
            {hasPermission(user, 'perm-send-bos') && (
              <ActionCard
                title="Vehicle Purchase Agreement"
                hint="Set the elections and generate the Vehicle Purchase Agreement. Moves the deal to Offer Accepted."
              /*
               * Generated but not sent is its own state, and it is not "not
               * started". The seller is sitting there having been told nothing.
               */
                status={step(deal.bill_of_sale_sent_at ? 'completed' : bosDoc ? 'failed' : 'not_started')}
                blockedReason={notApplicable}
                at={deal.bill_of_sale_sent_at}
                detail={
                  deal.bill_of_sale_sent_at
                    ? 'Sent to the seller ' + when(deal.bill_of_sale_sent_at)
                    : bosDoc
                      ? 'Generated, but the seller has not been told. Send it again.'
                      : null
                }
                last={last[ACTIONS.BOS_GENERATED] || null}
                count={counts[ACTIONS.BOS_GENERATED]}
                onRun={(repeat) => { setBosRepeatReason(repeat?.reason); setPanel('bos') }}
              />
            )}

            {hasPermission(user, 'perm-send-bos') && (
              <ActionCard
                title="Capture Signature"
                hint="The seller signs on this device. Moves the deal to Contract Signed."
                /* For a licensed seller, reaching Committed proves nothing about a
                   signature - the invoice carried the deal there. Only an actual
                   signature on file counts, or this card would claim a signing that
                   never happened. Types 1 and 2 keep the stage-derived answer they
                   have always had, and the detail line below still flags the case
                   where the stage says signed and nothing is on file. */
                status={step((licensedSeller ? signatureOnFile : signed) ? 'completed' : 'not_started')}
                blockedReason={notApplicable}
                detail={
                  signatureOnFile
                    ? 'The seller has signed'
                    : stageSaysSigned
                      ? 'Stage says signed - no signature on file'
                      : null
                }
                last={last[ACTIONS.SIGNATURE_CAPTURED] || null}
                count={counts[ACTIONS.SIGNATURE_CAPTURED]}
                onRun={() => setPanel('signature')}
              />
            )}

            {hasPermission(user, 'perm-upload-dd') && (
              <ActionCard
                title="DD Documents"
                hint="Collect and submit the due-diligence documents this seller type requires."
                status={step(ddApproved ? 'completed' : ddStarted ? 'in_progress' : 'not_started')}
                blockedReason={notApplicable}
                at={deal.dd_approved_at || deal.dd_submitted_at}
                detail={
                  deal.dd_status && deal.dd_status !== 'not_started'
                    ? 'Pack is ' + String(deal.dd_status).replace('_', ' ')
                    : null
                }
                last={last[ACTIONS.DD_COLLECTION_INITIATED] || null}
                count={counts[ACTIONS.DD_COLLECTION_INITIATED]}
                onRun={() => setPanel('dd')}
                extra={
                  deal.dd_waived_at ? (
                    <p className="ac-extra-note">
                      Documents waived {when(deal.dd_waived_at)}
                      {deal.dd_waived_reason ? ' — ' + deal.dd_waived_reason : null}
                    </p>
                  ) : (
                    <div className="ac-extra-row">
                      <input
                        type="text"
                        className="ac-extra-input"
                        value={waiveReason}
                        onChange={(e) => setWaiveReason(e.target.value)}
                        placeholder="Why this deal may sign without the pack"
                        disabled={waiving}
                      />
                      <button
                        type="button"
                        className="ac-extra-btn"
                        onClick={waiveDocuments}
                        disabled={waiving || waiveReason.trim().length < 5}
                      >
                        {waiving ? 'Recording…' : 'Waive documents'}
                      </button>
                    </div>
                  )
                }
              />
            )}

            {hasPermission(user, 'perm-book-inspection') && (
              <ActionCard
                title="Book Inspection"
                hint="Book a vendor to inspect the car, automatically or by hand."
                status={step(inspectionBooked ? 'completed' : 'not_started')}
                blockedReason={notApplicable}
                at={deal.inspection_booked_at}
                detail={
                  inspectionBooked
                    ? 'Booked - inspection ' + String(deal.inspection_status).replace('_', ' ')
                    : null
                }
                last={last[ACTIONS.INSPECTION_BOOKING_INITIATED] || null}
                count={counts[ACTIONS.INSPECTION_BOOKING_INITIATED]}
                onRun={() => setPanel('inspection')}
              />
            )}

            {hasPermission(user, 'perm-book-inspection') && (
              <ActionCard
                title="Inspection Outcome"
                hint="Record the third-party report: good, minor issues, or failed."
                status={
                  deal.inspection_outcome
                    ? 'completed'
                    : inspectionBooked
                    ? 'not_started'
                    : 'blocked'
                }
                blockedReason="No inspection has been booked for this car yet."
              detail={
                deal.inspection_outcome === 'failed'
                  ? 'Failed — vehicle does not meet purchase criteria. This deal cannot proceed.'
                  : deal.inspection_outcome === 'minor_issues' || deal.inspection_outcome === 'fail'
                    ? `Minor issues — proceeding anyway: ${deal.inspection_proceed_reason || 'no reason recorded'}`
                    : deal.inspection_outcome === 'pass'
                      ? 'Passed — the deal can be approved for payment.'
                      : null
                }
                at={deal.inspection_outcome_at}
                last={last[ACTIONS.INSPECTION_OUTCOME_RECORDED] || null}
                count={counts[ACTIONS.INSPECTION_OUTCOME_RECORDED]}
                busy={busy}
                onRun={() => setShowInspectionOutcome((v) => !v)}
              />
            )}

            {hasPermission(user, 'perm-issue-ar') && (
              <ActionCard
                title="Buyer Details"
                hint={
                  pipelineSideOf(deal.deal_status) === 'inflow'
                    ? 'Our own details, on the purchase contract. Already filled in from settings.'
                    : 'Who the car is being invoiced to. Without an email the invoice stays a draft.'
                }
                status={
                  deal.buyer_email ? 'completed' : deal.buyer_name ? 'in_progress' : 'not_started'
                }
                at={deal.buyer_captured_at}
                badge={deal.buyer_email ? null : 'NEEDED'}
                detail={
                  deal.buyer_name
                    ? (pipelineSideOf(deal.deal_status) === 'inflow' ? 'Buying as ' : 'Invoicing ') +
                      deal.buyer_name +
                      (deal.buyer_authorised_officer ? ' (' + deal.buyer_authorised_officer + ')' : '') +
                      (deal.buyer_email ? '' : ' - no email yet')
                    : null
                }
                last={last[ACTIONS.BUYER_DETAILS_SAVED] || null}
                count={counts[ACTIONS.BUYER_DETAILS_SAVED]}
                onRun={() => setPanel('buyer')}
              />
            )}

            {/* Its own card, because "signed" and "we hold the document" are different
                facts and were not the same one for six deals on this system. */}
            <ActionCard
              title="Signed Contract"
              hint="The signed Vehicle Purchase Agreement, kept on file. Required for six years."
              status={step(
                bosDoc && bosDoc.bos_pdf_path
                  ? 'completed'
                  : bosDoc && bosDoc.seller_signed_at
                  ? 'in_progress'
                  : 'not_started'
              )}
              blockedReason={notApplicable}
              at={bosDoc ? bosDoc.bos_pdf_filed_at || bosDoc.seller_signed_at : null}
              badge={bosDoc && bosDoc.seller_signed_at && !bosDoc.bos_pdf_path ? 'NOT FILED' : null}
              detail={
                bosDoc && bosDoc.bos_pdf_path
                  ? 'On file' +
                    (bosDoc.seller_signed_by_name ? ' - signed by ' + bosDoc.seller_signed_by_name : '')
                  : bosDoc && bosDoc.seller_signed_at
                  ? 'Signed, but the document is not on file yet'
                  : 'Nothing has been signed yet'
              }
              last={last[ACTIONS.BOS_DOCUMENT_FILED] || null}
              count={counts[ACTIONS.BOS_DOCUMENT_FILED]}
              onRun={() => setPanel('signed')}
              extra={
                bosDoc?.bos_pdf_path ? (
                  <button type="button" onClick={openSignedContract} disabled={openingContract}>
                    {openingContract ? 'Opening the contract...' : 'Open the signed contract'}
                  </button>
                ) : null
              }
            />

            {/*
             * Countersigning. The contract is not executed until it carries both
             * names, and until this session there was no way to add ours at all.
             */}
            <ActionCard
              title="Countersign"
              hint="Read what the seller signed, then put our name to it. The contract is executed once it carries both."
              status={
                bosDoc?.buyer_signed_at
                  ? 'completed'
                  : bosDoc?.seller_signed_at
                    ? 'not_started'
                    : 'blocked'
              }
              at={bosDoc?.buyer_signed_at || bosDoc?.seller_signed_at || null}
              badge={bosDoc?.buyer_signed_at && !bosDoc?.executed_pdf_path ? 'NOT FILED' : null}
              detail={
                bosDoc?.buyer_signed_at
                  ? 'Executed' + (bosDoc.buyer_signed_by_name ? ' - countersigned by ' + bosDoc.buyer_signed_by_name : '')
                  : bosDoc?.seller_signed_at
                    ? 'The seller has signed. Waiting on us.'
                    : null
              }
              blockedReason="The seller has not signed yet, so there is nothing to countersign."
              last={null}
              count={0}
              repeatable={false}
              onRun={() => setPanel('countersign')}
            />

            {hasPermission(user, 'perm-create-deal') && !deal.photo_url && (
              <ActionCard
                title={deal.generated_image_url ? 'Regenerate Image' : 'Generate Image'}
                hint="No photo on file - create an AI illustration to stand in for one."
                status={deal.generated_image_url ? 'completed' : 'not_started'}
                at={deal.generated_image_at}
                detail={deal.generated_image_url ? 'An illustration is on file' : null}
                last={last[ACTIONS.IMAGE_GENERATED] || null}
                count={counts[ACTIONS.IMAGE_GENERATED]}
                busy={imageBusy}
                onRun={(repeat) => {
                  void runAction(ACTIONS.IMAGE_GENERATED, makeImage, repeat)
                }}
              />
            )}

            {deal.generated_image_url && hasPermission(user, 'perm-create-deal') && (
              <ActionCard
                title="Remove Image"
                hint="Delete the AI illustration from this vehicle."
                last={last[ACTIONS.IMAGE_REMOVED] || null}
                count={counts[ACTIONS.IMAGE_REMOVED]}
                busy={imageBusy}
                onRun={(repeat) => {
                  void runAction(ACTIONS.IMAGE_REMOVED, dropImage, repeat)
                }}
              />
            )}

            {ACQUIRED_STATUSES.includes(deal.deal_status) && (
              <ActionCard
                title={deal.sale_price ? 'Sale' : 'Create Sale / Proposal'}
                hint="Price the vehicle, quote transport and pick the buyer."
                status={deal.sale_price ? 'completed' : 'not_started'}
                at={deal.sale_date}
                badge={deal.sale_margin_warning ? 'MARGIN' : null}
                detail={
                  deal.sale_price
                    ? 'Sold at $' + Number(deal.sale_price).toLocaleString('en-AU')
                    : null
                }
                onRun={() => setPanel('sale')}
              />
            )}

            {hasPermission(user, 'perm-create-deal') && (
              <ActionCard
                title="Purchase Agreement"
                hint="The details the purchase agreement prints - vehicle, seller, bank, payout, inclusions."
                last={last[ACTIONS.DEAL_EDITED] || null}
                count={counts[ACTIONS.DEAL_EDITED]}
                onRun={() => setPanel('agreement')}
              />
            )}
            {hasPermission(user, 'perm-create-deal') && (
              <ActionCard
                title="Edit Deal"
                hint="Correct the details, or archive the deal. Not a step you finish."
                last={last[ACTIONS.DEAL_EDITED] || null}
                count={counts[ACTIONS.DEAL_EDITED]}
                onRun={() => setPanel('edit')}
              />
            )}

            {deal.deal_status === 'approved_for_payment' && !deal.payment_run_id && (
              <ActionCard
                title="Initiate Payment Run"
                hint="Sends this deal to the Payment Run for the six due-diligence checks."
                status={deal.payment_run_id ? 'completed' : 'not_started'}
                last={last[ACTIONS.PAYMENT_RUN_INITIATED] || null}
                count={counts[ACTIONS.PAYMENT_RUN_INITIATED]}
                busy={busy}
                onRun={(repeat) => {
                  void runAction(ACTIONS.PAYMENT_RUN_INITIATED, initiatePaymentRun, repeat)
                }}
              />
            )}
            </div>

            <DealSmsHistory dealId={deal.id} />

            {nextStatuses.length > 0 && (
              <div className="workspace-advance">
                <span className="ws-label">Move stage</span>
                <div className="ws-advance-buttons">
                  {nextStatuses.map((s) => {
                    const allowed = canUserTransition(user, deal.deal_status as DealStatus, s)
                    const perm = permissionForTransition(deal.deal_status as DealStatus, s)
                    return (
                      <button
                        key={s}
                        className={`ws-advance-btn${isDestructiveTransition(s, deal.deal_status as DealStatus) ? ' danger' : ''}`}
                        disabled={busy || !allowed}
                        title={allowed ? undefined : `Requires the ${perm} permission`}
                        onClick={() => requestAdvance(s)}
                      >
                        {statusLabel(s)}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <PpsrLodgementPanel deal={deal} user={user} onChanged={onChanged} />

            <PayoutLetterPanel deal={deal} user={user} onChanged={onChanged} />

            <div className="workspace-history">
              <span className="ws-label">Workflow history</span>
              <WorkflowHistoryPanel dealId={deal.id} showTitle={false} />
            </div>
          </div>
        )
    }
  }

  return (
    <div className="workspace-overlay" onClick={onClose}>
      <div className="workspace-content" onClick={(e) => e.stopPropagation()}>
        <div className="workspace-header">
          <div>
            <h3>{dYear(deal)} {dMake(deal)} {dModel(deal)}</h3>
            <p className="workspace-sub">{deal.seller_name} · {money(dPrice(deal))} · VIN {dVin(deal) || '—'}</p>
          </div>
          <div className="workspace-header-actions">
            <button
              className="workspace-link"
              title="Copy a link to this deal"
              onClick={async () => {
                const url = `${window.location.origin}${pathForDeal(deal.id)}`
                try {
                  await navigator.clipboard.writeText(url)
                  setCopied(true)
                  window.setTimeout(() => setCopied(false), 1800)
                } catch {
                  say(url)
                }
              }}
            >
              {copied ? 'Link copied' : 'Copy link'}
            </button>
            <button className="workspace-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {panel !== 'overview' && (
          <button className="workspace-back" onClick={() => setPanel('overview')}>← Back to actions</button>
        )}

        {notice && (
          <div className={`workspace-notice${noticeTone === 'error' ? ' error' : ''}`}>{notice}</div>
        )}

        {showInspectionOutcome && (
          <div className="ws-inspection-outcome">
            <h5>Inspection outcome</h5>
            <p>
              A good report lets the deal move on. A poor one can still proceed, but §2.3 requires
              the reason to be recorded against the deal — or you cancel instead.
            </p>
            <div className="ws-io-report">
              <span className="ws-label">Inspection report</span>
              {inspectionReport ? (
                <p>
                  Attached:{' '}
                  <button type="button" className="workspace-link" onClick={openInspectionReport}>
                    {inspectionReport.name || 'open the report'}
                  </button>
                </p>
              ) : (
                <p>No report attached yet.</p>
              )}
              <FileDropZone
                accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                label="Attach report"
                busy={reportBusy}
                busyLabel="Attaching the report…"
                onFiles={(files) => uploadInspectionReport(files[0])}
              />
              <small>
                {reportBusy
                  ? 'Attaching the report...'
                  : 'Attach the report before recording the outcome, so the words below can be checked against it. Uploading again keeps the earlier one on the deal.'}
              </small>
            </div>
            <div className="ws-io-actions">
              <button className="ws-advance-btn" disabled={busy} onClick={() => recordInspectionOutcome('pass')}>
                Inspection Result: Good — no issues found
              </button>
            </div>
            <label htmlFor="ws-fail-reason">Proceeding despite a poor report — why?</label>
            <textarea
              id="ws-fail-reason"
              rows={2}
              value={failReason}
              onChange={(e) => setFailReason(e.target.value)}
              placeholder="e.g. minor cosmetic damage, price renegotiated to $28,500"
            />
            <div className="ws-io-actions">
              <button
                className="ws-advance-btn danger"
                disabled={busy || !failReason.trim()}
                onClick={() => recordInspectionOutcome('minor_issues', failReason)}
              >
                Inspection Result: Minor Issues — proceed with reason
              </button>
            {/*
               Winnie's third outcome. It takes no reason field: a reason is what you
               record when you are proceeding anyway, and this outcome means the deal
               does not proceed. The status gate in dealStatusService refuses to
               advance a deal carrying it.
            */}
            <button
              className="ws-advance-btn danger"
              disabled={busy}
              onClick={() => recordInspectionOutcome('failed', '')}
            >
              Inspection Result: Failed — vehicle does not meet purchase criteria, deal should not proceed
            </button>              <button className="ws-advance-btn" onClick={() => setShowInspectionOutcome(false)}>
                Close
              </button>
            </div>
          </div>
        )}

        {confirmTarget && (
          <div className="ws-confirm" role="alertdialog">
            <div className="ws-confirm-card">
              <h4>Cancel this deal?</h4>
              <p>
                {dYear(deal)} {dMake(deal)} {dModel(deal)} will move to{' '}
                <strong>{statusLabel(confirmTarget)}</strong>. A cancelled deal has no valid next
                stage and cannot be reopened from the pipeline.
              </p>
              {/* Winnie, 18 Sep: a return to the seller is not a new status - it reuses this
                  Cancelled action, with a VehicleReturn as the structured record of why. So it
                  is offered here, where somebody is already cancelling, rather than as a
                  separate button they have to know about. */}
              <p className="ws-confirm-aside">
                Giving the vehicle back to the seller because of a defect? Record it as a return
                instead — it voids the cost lines, releases any PPSR registration and, on a
                tax-invoice purchase, posts the GST credit reversal in this period.
              </p>
              <div className="ws-confirm-actions">
                <button className="ws-advance-btn" onClick={() => setConfirmTarget(null)}>Keep the deal</button>
                <button
                  className="ws-advance-btn"
                  onClick={() => {
                    setConfirmTarget(null)
                    setShowReturn(true)
                  }}
                >
                  Return to seller…
                </button>
                <button className="ws-advance-btn danger" disabled={busy} onClick={() => advance(confirmTarget)}>
                  Yes, cancel it
                </button>
              </div>
            </div>
          </div>
        )}

        {showReturn && (
          <VehicleReturnDialog
            deal={deal}
            user={user}
            onClose={() => setShowReturn(false)}
            onDone={() => {
              setShowReturn(false)
              onClose()
            }}
          />
        )}

        <div className="workspace-body">{renderPanel()}</div>
      </div>
    </div>
  )
}
